import { FormConfig, Section, QuestionFilterStrategy } from '../../types/form';
import { MoldOrder, MoldOrderFirstLevelType } from '../../types/mold';
import {
  StepConfig,
  StepComponentType,
  UploadVariant,
} from '../../types/steps';
import { filterVisibleSteps } from './utils/stepVisibility';
import {
  orderTemplateService,
  OrderTemplateByIdResponse,
} from '../../services/api';
import { resolveCompanyCode } from '../../utils/company';

export interface ProcessedFormConfig {
  sections: Section[];
  questionFilterStrategy?: QuestionFilterStrategy;
}

export interface ImageConfig {
  id: string;
  tags: string[];
}

export interface LoadedFormConfig {
  sections: Section[];
  questionFilterStrategy?: QuestionFilterStrategy;
  steps: StepConfig[];
  visibleSteps: StepConfig[];
  images?: ImageConfig[];
  /**
   * Display version of the REVISION this config resolved from — the published
   * versionLabel (e.g. "1.3"), falling back to "r<revision>" for an unlabeled
   * revision. Surfaced in the questionnaire header and the printed PDF so every
   * rendered form names the definition version it came from.
   */
  templateVersionLabel?: string;
}

type RawFormConfig = Partial<FormConfig> & Pick<FormConfig, 'sections'>;

const processFormConfig = (jsonConfig: RawFormConfig): ProcessedFormConfig => ({
  ...jsonConfig,
  sections: jsonConfig.sections.map((section: Section) => ({ ...section })),
});

const getDefaultStepConfig = (): StepConfig[] => [
  {
    id: 1,
    titleSuffix: 'ก่อน - ถ่ายรูปแม่พิมพ์',
    componentType: StepComponentType.BEFORE_UPLOAD,
    uploadVariant: UploadVariant.GUIDED,
    props: { prefix: 'before' },
  },
  {
    id: 2,
    titleSuffix: 'ตรวจสภาพแม่พิมพ์',
    componentType: StepComponentType.QUESTIONNAIRE,
  },
  {
    id: 3,
    titleSuffix: 'หลัง - ถ่ายรูปแม่พิมพ์',
    componentType: StepComponentType.AFTER_UPLOAD,
    uploadVariant: UploadVariant.GUIDED,
    props: { prefix: 'after' },
  },
];

// Cache for loaded configs
const configCache: Record<string, LoadedFormConfig> = {};

export const clearFormConfigCache = () => {
  Object.keys(configCache).forEach((key) => delete configCache[key]);
};

// Build the fully-processed LoadedFormConfig from a raw definition object. The DB
// schema and the bundled JSON have the identical shape (the DB was seeded from the
// JSON), so both paths run exactly this same processing — only the source differs.
const buildLoadedFormConfig = (
  rawConfig: RawFormConfig,
  moldOrder: MoldOrder,
): LoadedFormConfig => {
  const processed = processFormConfig(rawConfig);
  const steps =
    rawConfig.steps && Array.isArray(rawConfig.steps)
      ? rawConfig.steps
      : getDefaultStepConfig();
  return {
    ...processed,
    steps,
    visibleSteps: filterVisibleSteps(steps, moldOrder),
  };
};

// Resolve the bound order_template revision id. The backend emits the FK nested as
// orderTemplate.id; the flat orderTemplateId is accepted too. An unbound order (id 0 or
// absent — a create-preview, or a legacy order) resolves to undefined → the DB
// published-by-name path.
const resolveOrderTemplateId = (moldOrder: MoldOrder): number | undefined => {
  const raw = moldOrder?.orderTemplateId ?? moldOrder?.orderTemplate?.id;
  return typeof raw === 'number' && raw > 0 ? raw : undefined;
};

export const loadFormConfigWithSteps = async (
  moldOrder: MoldOrder,
  moldOrderGroup: MoldOrderFirstLevelType,
): Promise<LoadedFormConfig> => {
  const version = moldOrder?.orderDetail?.formVersion || '1.0';
  const moldOrderType = moldOrderGroup;
  const orderTemplateId = resolveOrderTemplateId(moldOrder);
  // Match cache key logic from useStepConfig: type-version-id-status, plus the bound
  // template id so a bound (by-id) and an unbound (published-by-name) definition never
  // collide in the cache.
  //
  // KNOWN TRADEOFF — mold.updatedAt in the key: a concurrent mold update (another user,
  // a background sync) changes updatedAt, busts this cache, and the next load rebuilds
  // the config mid-fill. For a BOUND order the schema itself cannot change (the revision
  // is immutable, fetched by id), so a rebuild is content-identical and harmless; only
  // an UNBOUND order (published-by-name) could pick up a newly-published definition
  // mid-fill, where applyAnswerSideEffects may then clear answers to questions the new
  // definition hides. Accepted because unbound orders are a shrinking legacy cohort
  // (the backfill binds them) and mold updates during an active fill are rare; revisit
  // if either assumption breaks.
  const moldChanges = moldOrder?.mold?.updatedAt ?? 'no-mold-change';
  const cacheKey = `${moldOrderType}-${version}-${moldOrder.id}-${moldOrder.status}-${moldChanges}-${orderTemplateId ?? 'published'}`;
  if (configCache[cacheKey]) {
    return configCache[cacheKey];
  }

  // Shared post-fetch tail for both the bound and published paths: a missing schema
  // fails closed, and the resolved definition is processed + cached identically.
  const finalizeFromDetail = (
    detail:
      | Pick<OrderTemplateByIdResponse, 'schema' | 'versionLabel' | 'revision'>
      | undefined,
    errorMessage: string,
  ): LoadedFormConfig => {
    const schema = detail?.schema;
    // Fail closed on a malformed revision too: a schema object lacking a `sections`
    // array would otherwise reach processFormConfig's `sections.map` and throw an
    // opaque "Cannot read properties of undefined" instead of this clear message.
    if (!detail || !schema || !schema.sections) {
      throw new Error(errorMessage);
    }
    const rawConfig: RawFormConfig = { ...schema, sections: schema.sections };
    const result = buildLoadedFormConfig(rawConfig, moldOrder);
    // Name the resolved revision for display: published label, or rN for an
    // unlabeled one. Older backends omit both fields — then leave it undefined
    // (the UI simply hides the version line).
    result.templateVersionLabel =
      detail.versionLabel ??
      (typeof detail.revision === 'number' ? `r${detail.revision}` : undefined);
    configCache[cacheKey] = result;
    return result;
  };

  // Bound orders (positive order_template_id) render from the DB-served definition.
  // Fail closed here: a bound order must never silently render/save against the
  // legacy bundled JSON if its bound DB revision cannot be loaded.
  if (orderTemplateId) {
    let response: Awaited<
      ReturnType<typeof orderTemplateService.getOrderTemplateById>
    >;
    try {
      response =
        await orderTemplateService.getOrderTemplateById(orderTemplateId);
    } catch (error) {
      throw Object.assign(
        new Error(`Unable to load bound order template ${orderTemplateId}`),
        { cause: error },
      );
    }

    return finalizeFromDetail(
      response?.data,
      `Unable to load bound order template ${orderTemplateId}`,
    );
  }

  // Unbound order (no positive order_template_id): a not-yet-persisted create-preview,
  // or a legacy order that predates binding. Render from the DB's CURRENTLY-PUBLISHED
  // revision for this company + order-type group — the same revision binding-on-creation
  // pins, so the preview matches what the saved order will render as. There is no bundled
  // JSON fallback any more, so fail closed if the published definition cannot be loaded.
  const companyCode = resolveCompanyCode();
  const name = `${companyCode}/${moldOrderType}`;
  let publishedResponse: Awaited<
    ReturnType<typeof orderTemplateService.getPublishedOrderTemplate>
  >;
  try {
    publishedResponse =
      await orderTemplateService.getPublishedOrderTemplate(name);
  } catch (error) {
    throw Object.assign(
      new Error(`Unable to load published order template "${name}"`),
      { cause: error },
    );
  }

  return finalizeFromDetail(
    publishedResponse?.data,
    `Unable to load published order template "${name}"`,
  );
};
